import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import ParentContainor from '../../components/base/ParentContainor';
import LeaderboardScreen from '../../components/LeaderboardScreen';
import { ROUTE_ENUM } from '../../routes/RoutesConstant';
import { getRapFireLeaderboard } from '../../network/network_callbacks';
import { TEXT_LABELS } from '../../constants/TextLabelConstants';
import { PAGE_OUTER_PADDING } from '../../constants/housieLiveStyles';
import {
  extractLeaderboardPlayers,
  getPlayerPoints,
  normalizeLeaderboardPlayer,
} from '../../utils/utils';

const rankLeaderboardPlayers = (players = []) => {
  const normalized = players.map((user, index) =>
    normalizeLeaderboardPlayer(user, index)
  );
  const hasApiRank = normalized.some((player) => Number(player.rank) > 0);
  if (hasApiRank) {
    return [...normalized].sort(
      (a, b) => Number(a.rank || 0) - Number(b.rank || 0)
    );
  }
  return [...normalized]
    .sort((a, b) => getPlayerPoints(b) - getPlayerPoints(a))
    .map((user, index) => ({
      ...user,
      rank: index + 1,
    }));
};

const RapFireLeaderBoard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { contestId } = location.state || {};

  const canvasRef = React.useRef(null);
  const [showBoard, setShowBoard] = useState(false);
  const [leaderBoardData, setLeaderBoardData] = useState([]);

  useEffect(() => {
    getRapFireLeaderBoardApiData();
    return () => {
      setShowBoard(false);
      setLeaderBoardData([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contestId]);

  const getRapFireLeaderBoardApiData = async () => {
    try {
      const response = await getRapFireLeaderboard(contestId);
      if (response?.data?.data && response?.data?.responseCode === 200) {
        const data = response?.data?.data || {};
        const participants = extractLeaderboardPlayers(data);
        setLeaderBoardData(participants);

        const winners = rankLeaderboardPlayers(participants).filter(
          (p) => getPlayerPoints(p) > 0
        ).slice(0, 3);

        if (participants.length > 0 && winners.length > 0) {
          if (canvasRef.current) {
            const myConfetti = confetti.create(canvasRef.current, {
              resize: true,
              useWorker: true,
            });
            myConfetti({
              startVelocity: 80,
              particleCount: 800,
              spread: 360,
              ticks: 10000,
              colors: ['#FF00F2', '#8800D1', '#FFFFFF', '#FF60AD', '#C000B0'],
            });
            setTimeout(() => setShowBoard(true), 2200);
          } else {
            setShowBoard(true);
          }
        } else {
          setShowBoard(true);
        }
      } else {
        setShowBoard(true);
      }
    } catch (error) {
      console.error('❌ Failed to getRapFireLeaderBoardApiData : ', error);
      setShowBoard(true);
    }
  };

  const rankedUsers = useMemo(
    () => rankLeaderboardPlayers(leaderBoardData),
    [leaderBoardData]
  );

  const hasWinners = rankedUsers.some((player) => getPlayerPoints(player) > 0);

  return (
    <ParentContainor
      route={ROUTE_ENUM.RAP_FIRE_GAME}
      parentContainorStyle={{ justifyContent: 'flex-start' }}
      isShowHeader={false}
      showCopyRightText
      isBreadcrumbVisible={false}
      padding={PAGE_OUTER_PADDING}
      isShowRapFireLeaderboardSpotlight={
        !showBoard && hasWinners && leaderBoardData?.length > 0
      }
      showBlurredBg
    >
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 999,
        }}
      />

      <LeaderboardScreen
        headerTitle={`${TEXT_LABELS.RAP_FIRE_WELCOME_TITLE_BRAND} - ${TEXT_LABELS.RAP_FIRE_LEADERBOARD_TITLE}`}
        onBack={() => navigate(ROUTE_ENUM.SELECT_GAME.routePath)}
        columns={{
          rank: TEXT_LABELS.RAP_FIRE_LEADERBOARD_COL_RANK,
          player: TEXT_LABELS.RAP_FIRE_LEADERBOARD_COL_PLAYER,
          points: TEXT_LABELS.RAP_FIRE_LEADERBOARD_COL_GAME_POINTS,
          won: TEXT_LABELS.RAP_FIRE_LEADERBOARD_COL_WON,
        }}
        players={rankedUsers}
        showBoard={showBoard}
        hasWinners={hasWinners}
        emptyLabel={TEXT_LABELS.RAP_FIRE_LEADERBOARD_NO_WINNERS}
        congratsLabel={
          hasWinners
            ? TEXT_LABELS.RAP_FIRE_LEADERBOARD_WINNER_WISHES
            : TEXT_LABELS.RAP_FIRE_LEADERBOARD_NO_WINNERS
        }
      />
    </ParentContainor>
  );
};

export default RapFireLeaderBoard;
